import { createContext, ReactElement, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Connection, MeetDomainEvents } from '../../Meet';
import { VideoCallEvents } from '../domain/events';
import { publish, subscribe, unsubscribe } from '../../EventsBus/services/service';
import type { Publisher, Event as OpenTokEvent, Stream, Session } from '@opentok/client';
import { useIsMobile } from '../../../app/hooks/useIsMobile';
import { ChatEvents } from '../../Chat/domain/events';
import { MediaDomainEvents } from '../../Media/domain/events';
import { SESSION_RECOVERY_MAX_ATTEMPTS, SESSION_RECOVERY_RETRY_DELAY_MS } from '../domain/constants';

export interface VideoCallContextProviderProps {
    children: ReactElement;
}

export interface VideoCallContextValue {
    isParticipantJoined: boolean;
    sessionRef: RefObject<Session | null>;
    participantMuted: boolean;
    participantHiddenCam: boolean;
    participantScreenSharing: boolean;
    showParticipantPlaceholder: boolean;
    capabilitiesAlert: boolean;
    createSession: (connection: Connection) => void;
    joinSession: (publisher: Publisher) => void;
    disconnectSession: () => void;
    startSessionListeners: () => void;
    stopSessionListeners: () => void;
    participantJoined: (event: OpenTokEvent<'streamCreated', Publisher> & {
      stream: Stream;
    }) => void;
    sendChatMessage: (message: string) => void;
    sendMuted: () => void;
    sendUnmuted: () => void;
    toggleCapabilitiesAlert: (value: boolean) => void;
}

export const VideoCallContext = createContext<VideoCallContextValue | undefined>(undefined);

export const VideoCallContextProvider = ({ children }: VideoCallContextProviderProps) => {
	const isMobile = useIsMobile();

	const sessionRef = useRef<Session | null>(null);
	const lastConnectionRef = useRef<Connection | null>(null);
	const recoveryAttemptsRef = useRef(0);
	const recoveryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const recoverSessionRef = useRef<() => void>(() => {});

	const [participantScreenSharing, setParticipantScreenSharing] = useState(false);
	const [participantHiddenCam, setParticipantHiddenCam] = useState(false);
	const [participantMuted, setParticipantMuted] = useState(false);
	const [isParticipantJoined, setIsParticipantJoined] = useState(false);
	const [capabilitiesAlert, setCapabilitiesAlert] = useState(false);

	const participantJoined = useCallback((event: OpenTokEvent<'streamCreated', Publisher> & {
      stream: Stream;
    }) => {
		if (!sessionRef.current) {
			return;
		}
  
		sessionRef.current.subscribe(
			event.stream,
			'meet-guest',
			{
				insertMode: 'append',
				fitMode: isMobile ? 'cover' : 'contain',
				width: '100%',
				height: '100%',
				showControls: false,
				style: { buttonDisplayMode: 'off' },
			},
			(err) => {
				if (err) {
					publish(VideoCallEvents.participantJoinSubscriptionFailed);
					return;
				}

				function onVideoDisabled() {
					setParticipantHiddenCam(true);
					publish(VideoCallEvents.participantVideoDisabled);
				}

				function onVideoEnabled() {
					setParticipantHiddenCam(false);
					publish(VideoCallEvents.participantVideoEnabled);
				}

				publish(VideoCallEvents.participantJoinSubscriptionSuccess);

				// @ts-ignore
				sessionRef.current?.streams.forEach((subRaw: Stream) => {
					const subArr = sessionRef.current?.getSubscribersForStream(subRaw);
  
					if (subArr?.length) {
						setParticipantMuted(!subRaw.hasAudio);
						if (subRaw.hasAudio) {
							publish(VideoCallEvents.participantAudioEnabled);
						} else {
							publish(VideoCallEvents.participantAudioDisabled);
						}

						setParticipantHiddenCam(!subRaw.hasVideo);
						if (subRaw.hasVideo) {
							publish(VideoCallEvents.participantVideoEnabled);
						} else {
							publish(VideoCallEvents.participantVideoDisabled);
						}

						subArr[0].off('videoDisabled');
						subArr[0].off('videoEnabled');
						subArr[0].off('audioDisabled');
						subArr[0].off('audioEnabled');
  
						subArr[0].on('videoDisabled', onVideoDisabled);
						subArr[0].on('videoEnabled', onVideoEnabled);
					}
				});
			}
		);
	}, [sessionRef]);

	const participantScreenSharingStarted = useCallback((event: OpenTokEvent<'streamCreated', Publisher> & {
      stream: Stream;
    }) => {
		sessionRef.current?.subscribe(
			event.stream,
			'screen-participant',
			{
				insertMode: 'append',
				fitMode: 'contain',
				width: '100%',
				height: '100%',
				showControls: false,
				style: { buttonDisplayMode: 'off' },
			},
			(err) => {
				if (err) {
					publish(VideoCallEvents.participantScreenSharingStartFailed, {
						error: err
					});

					return;
				}

				setParticipantScreenSharing(true);
				publish(VideoCallEvents.participantScreenSharingConnected);
			}
		);
	}, [sessionRef]);

	const resetParticipantState = useCallback(() => {
		setIsParticipantJoined(false);
		setParticipantScreenSharing(false);
		setParticipantHiddenCam(false);
		setParticipantMuted(false);
	}, []);

	const startSessionListeners = useCallback(() => {
		if (!sessionRef.current) {
			return;
		}

		const session = sessionRef.current;

		session.on('sessionReconnecting', () => {
			publish(VideoCallEvents.sessionReconnecting);
		});

		session.on('sessionReconnected', () => {
			publish(VideoCallEvents.sessionReconnected);
		});

		session.on('sessionDisconnected', (event) => {
			if (session !== sessionRef.current) {
				return;
			}

			resetParticipantState();
			publish(VideoCallEvents.sessionLost, { reason: event.reason });

			if (event.reason === 'networkDisconnected') {
				recoverSessionRef.current();
			}
		});

		// @ts-ignore
		sessionRef.current.on('streamCreated', (event: OpenTokEvent<'streamCreated', Publisher> & {
          stream: Stream;
        }) => {
			if (event.stream.videoType === 'screen') {
				participantScreenSharingStarted(event);
				publish(VideoCallEvents.participantScreenSharingStarted, {
					event: event
				});
			} else {
				setIsParticipantJoined(true);
				publish(VideoCallEvents.participantJoined, {
					event: event
				});
			}
		});
    
		// @ts-ignore
		sessionRef.current.on('streamDestroyed', (event: OpenTokEvent<'streamDestroyed', Publisher> & {
          stream: Stream;
        }) => {
			if (event.stream.videoType === 'screen') {
				setParticipantScreenSharing(false);
				publish(VideoCallEvents.participantScreenSharingDisconnected);
			} else {
				setIsParticipantJoined(false);
				publish(VideoCallEvents.participantLeft);
			}
		});

		// @ts-ignore
		sessionRef.current.on('signal:chat', (event: Event<`signal:${string}`, Session> & {
          type?: string;
          data?: string;
          from: Connection | null;
        }) => {
			const origin = event.from.connectionId === sessionRef.current?.connection?.connectionId ? 'mine' : 'theirs';

			if (origin === 'theirs') {
				publish(ChatEvents.chatMessageIncome, {
					message: event.data,
					origin,
				});
			}
		});

		// @ts-ignore
		sessionRef.current.on('signal:finish', (event: Event<`signal:${string}`, Session> & {
          type?: string;
          data?: string;
          from: Connection | null;
        }) => {
			if (event.from.connectionId === sessionRef.current?.connection?.connectionId) {
				return;
			}
          
			publish(MeetDomainEvents.forcedToFinish);
		});

		// @ts-ignore
		sessionRef.current.on('signal:muted', (event: Event<`signal:${string}`, Session> & {
          type?: string;
          data?: string;
          from: Connection | null;
        }) => {
			if (event.from.connectionId === sessionRef.current?.connection?.connectionId) {
				return;
			}

			setParticipantMuted(true);
			publish(VideoCallEvents.participantAudioDisabled);
		});

		// @ts-ignore
		sessionRef.current.on('signal:unmuted', (event: Event<`signal:${string}`, Session> & {
          type?: string;
          data?: string;
          from: Connection | null;
        }) => {
			if (event.from.connectionId === sessionRef.current?.connection?.connectionId) {
				return;
			}
			setParticipantMuted(false);
			publish(VideoCallEvents.participantAudioEnabled);
		});
	}, [sessionRef]);

	const stopSessionListeners = useCallback(() => {
		if (!sessionRef.current) {
			return;
		}

		sessionRef.current.off('sessionReconnecting');
		sessionRef.current.off('sessionReconnected');
		sessionRef.current.off('sessionDisconnected');
		sessionRef.current.off('streamCreated');
		sessionRef.current.off('streamDestroyed');
		sessionRef.current.off('signal:chat');
		sessionRef.current.off('signal:finish');
		sessionRef.current.off('signal:muted');
		sessionRef.current.off('signal:unmuted');
	}, [sessionRef]);

	const checkCapabilities = useCallback(() => {
		if (!sessionRef.current) {
			return;
		}

		let capabilitiesKo = Object.values(sessionRef.current?.capabilities).some(value => value === 0);

		if (capabilitiesKo) {
			setCapabilitiesAlert(true);
			publish(VideoCallEvents.publisherCapabilitiesKo);
			return;
		}

		publish(VideoCallEvents.publisherCapabilitiesOk);
	}, [sessionRef]);

	const createSession = useCallback(async ({ apiKey, sessionId, token }: Connection) => {
		if (!apiKey || !sessionId) {
			publish(VideoCallEvents.sessionCreationFailedByCredentials);
			throw new Error('Check your Vonage credentials');
		}

		lastConnectionRef.current = { apiKey, sessionId, token };

		const { initSession } = await import('@opentok/client');
		sessionRef.current = initSession(apiKey, sessionId);
		publish(VideoCallEvents.sessionCreated);
		startSessionListeners();
		sessionRef.current.connect(token, (error) => {
			if (error) {
				publish(VideoCallEvents.sessionConnectionFailed, { error: error });

				if (recoveryAttemptsRef.current > 0) {
					recoveryTimeoutRef.current = setTimeout(() => {
						recoverSessionRef.current();
					}, SESSION_RECOVERY_RETRY_DELAY_MS);
				}
				return;
			}

			recoveryAttemptsRef.current = 0;
			checkCapabilities();
			publish(VideoCallEvents.sessionConnected);
		});
	},[sessionRef]);

	const teardownSession = useCallback(() => {
		if (!sessionRef.current) {
			return;
		}
		stopSessionListeners();
		try {
			sessionRef.current.disconnect();
		} catch (err) {
			console.error('Session disconnect error:', err);
		}
		sessionRef.current = null;
		publish(VideoCallEvents.sessionDisconnected);
	}, [sessionRef]);

	const disconnectSession = useCallback(
		() => {
			if (recoveryTimeoutRef.current) {
				clearTimeout(recoveryTimeoutRef.current);
				recoveryTimeoutRef.current = null;
			}
			recoveryAttemptsRef.current = 0;
			teardownSession();
		},[sessionRef]);

	const recoverSession = useCallback(() => {
		const connection = lastConnectionRef.current;

		if (!connection) {
			return;
		}

		if (recoveryAttemptsRef.current >= SESSION_RECOVERY_MAX_ATTEMPTS) {
			publish(VideoCallEvents.sessionRecoveryFailed, { attempts: recoveryAttemptsRef.current });
			recoveryAttemptsRef.current = 0;
			return;
		}

		recoveryAttemptsRef.current += 1;
		recoveryTimeoutRef.current = null;
		publish(VideoCallEvents.sessionRecovering, { attempt: recoveryAttemptsRef.current });

		teardownSession();
		createSession(connection).catch((error) => {
			publish(VideoCallEvents.sessionConnectionFailed, { error });
		});
	}, [sessionRef]);

	recoverSessionRef.current = recoverSession;

	const joinSession = useCallback(async (publisher: Publisher) => {
		if (!sessionRef.current) {
			return;
		}
		publish(VideoCallEvents.publisherAttemptToConnect);

		sessionRef.current.publish(publisher, (error) => {
			if (error) {
				publish(VideoCallEvents.publisherConnectionFailed, { error: error });
			} else {
				publish(VideoCallEvents.publisherConnected);
			}
		});
	}, [sessionRef]);

	const onStartScreenSharing = useCallback((event: CustomEvent<{ publisher: Publisher }>) => {
		sessionRef.current?.publish(event.detail.publisher);
		publish(VideoCallEvents.publisherScreenSharingConnected);
	}, [sessionRef]);

	const onStopScreenSharing = useCallback((event: CustomEvent<{ publisher: Publisher }>) => {
		sessionRef.current?.unpublish(event.detail.publisher);
		publish(VideoCallEvents.publisherScreenSharingDisconnected);
	}, [sessionRef]);

	const showParticipantPlaceholder = useMemo(() => {
		return (!isParticipantJoined || participantHiddenCam) && !participantScreenSharing;
	}, [isParticipantJoined, participantHiddenCam]);

	const sendChatMessage = useCallback((message: string) => {
		if (!sessionRef?.current?.connection) {
			return;
		}
		
		publish(VideoCallEvents.participantChatMessageSending);
		publish(ChatEvents.chatMessageIncome, {
			message,
			origin: 'mine',
		});
		
		sessionRef.current.signal(
			{
				type: 'chat',
				data: message,
			},
			(err) => {
				if (err) {
					publish(VideoCallEvents.participantChatMessageSendFailed, {
						error: err
					});
					return;
				}

				publish(VideoCallEvents.participantChatMessageSent);
			}
		);
	}, [sessionRef]);

	const sendMuted = useCallback(() => {
		sessionRef.current?.signal({
			type: 'muted',
		});
	}, [sessionRef]);
      
	const sendUnmuted = useCallback(() => {
		sessionRef.current?.signal({
			type: 'unmuted',
		});
	}, [sessionRef]);

	const hangUpAndFinish = useCallback(() => {
		if (!sessionRef.current) {
			return;
		}

		sessionRef.current.signal({
			type: 'finish',
		});
	}, [sessionRef]);

	const sendEvent = useCallback((event: string, data?: Object) => {
		if (!sessionRef?.current?.connection) {
			return;
		}

		sessionRef.current.signal({
			type: event,
			data: JSON.stringify(data)
		}, (err) => {
			if (err) {
				publish(VideoCallEvents.sendEventToVonageFailed, { error: err });
			}

			publish(VideoCallEvents.sentEventToVonage, { event, data });
		});
	}, [sessionRef]);

	const toggleCapabilitiesAlert = useCallback(() => {
		setCapabilitiesAlert(!capabilitiesAlert);
	}, []);
  
	useEffect(() => {
		subscribe(VideoCallEvents.publisherScreenSharingStarted, onStartScreenSharing);
		subscribe(VideoCallEvents.publisherScreenSharingStopped, onStopScreenSharing);
		subscribe(MeetDomainEvents.hangUpAndFinish, hangUpAndFinish);

		return () => {
			unsubscribe(VideoCallEvents.publisherScreenSharingStarted, onStartScreenSharing);
			unsubscribe(VideoCallEvents.publisherScreenSharingStopped, onStopScreenSharing);
			unsubscribe(MeetDomainEvents.hangUpAndFinish, hangUpAndFinish);
		};
	}, [sessionRef]);

	useEffect(() => {
		function publisherDestroyedCallback() {
			disconnectSession();
		}

		subscribe(VideoCallEvents.publisherDestroyed, publisherDestroyedCallback);

		return () => {
			unsubscribe(VideoCallEvents.publisherDestroyed, publisherDestroyedCallback);
		};
	}, []);

	useEffect(() => {
		function unsubscribeEvent(event: string) {
			unsubscribe(event, () => {
				sendEvent(event, {},);
			}, true);
		};

		const eventsList = [
			MeetDomainEvents.waitingRoomReady,
			MeetDomainEvents.hangUpAndFinish,
			MediaDomainEvents.acceptedMediaPermissions,
			MediaDomainEvents.alreadyAcceptedMediaPermissions,
			VideoCallEvents.publisherCapabilitiesKo,
			VideoCallEvents.publisherCapabilitiesOk,
			VideoCallEvents.participantJoined,
			VideoCallEvents.participantJoinSubscriptionSuccess,
			VideoCallEvents.participantJoinSubscriptionFailed,
		];

		eventsList.forEach((event) => {
			subscribe(event, () => {
				sendEvent(event, {});
			}, true);
		});
    
		return () => {
			eventsList.forEach(unsubscribeEvent);
		};
	}, []);

	useEffect(() => {
		return () => {
			if (recoveryTimeoutRef.current) {
				clearTimeout(recoveryTimeoutRef.current);
			}
			try {
				sessionRef.current?.disconnect();
			} catch (err) {
				console.error('Session disconnect error:', err);
			}
		};
	}, []);

	const value = useMemo(() => {
		return { 
			sessionRef,
			isParticipantJoined,
			participantMuted,
			participantHiddenCam,
			participantScreenSharing,
			showParticipantPlaceholder,
			capabilitiesAlert,
			toggleCapabilitiesAlert,
			createSession,
			joinSession,
			disconnectSession,
			startSessionListeners,
			stopSessionListeners,
			participantJoined,
			sendChatMessage,
			sendMuted,
			sendUnmuted
		};
	}, [sessionRef, isParticipantJoined, participantMuted, participantHiddenCam, participantScreenSharing, showParticipantPlaceholder, capabilitiesAlert]);

	return <VideoCallContext.Provider value={value}>{children}</VideoCallContext.Provider>;
};

export const useVideoCallContext = () => {
	const context = useContext(VideoCallContext);
	if (!context) {
		throw new Error('useVideoCallContext must be used within a VideoCallContextProvider');
	}
	return context;
};